import { canReadEscalations } from '../../lib/permissions/escalations';
import { canReadJustifications } from '../../lib/permissions/justifications';
import { canReadKnowledge } from '../../lib/permissions/knowledge';
import { canReadReplacements } from '../../lib/permissions/replacements';
import type { NotificationType } from '../../lib/api/notifications';

/**
 * The bell panel's type→screen map (M-16, Task 3, spec §3) — where a
 * notification's row links, or `null` when it should render as plain text
 * instead of a link.
 *
 * `null` COVERS TWO DIFFERENT CASES, DELIBERATELY COLLAPSED INTO ONE
 * RETURN VALUE:
 *
 * 1. The type is not one of the six this map knows. A future module will
 *    write a seventh type before this map learns it, and the panel must
 *    render that row as inert text, never throw and take the whole shell
 *    down with it.
 * 2. The type IS known, but the caller cannot read the destination module.
 *    Linking there would be a click into a `403`. The gate for each
 *    destination is the SAME `canRead*` helper the nav filter
 *    (`src/app/App.tsx`) uses to decide whether the module renders at
 *    all — imported here, never restated. Three copies of one rule is how
 *    `escalation.view` hid the escalations feature three times in one
 *    milestone; this function is not becoming a fourth copy.
 */
const DESTINATION_BY_TYPE: Record<NotificationType, string> = {
  'escalation.assigned': '/escalations',
  'justification.raised': '/justifications',
  'justification.answered': '/justifications',
  'justification.decided': '/justifications',
  'knowledge.published': '/knowledge-base',
  'replacement.requested': '/replacements',
};

/** Whether `permissions` may read the module a given path leads to — the SAME rule each screen and the nav gate itself enforce. */
const CAN_READ_PATH: Record<string, (permissions: readonly string[]) => boolean> = {
  '/escalations': canReadEscalations,
  '/justifications': canReadJustifications,
  '/knowledge-base': canReadKnowledge,
  '/replacements': canReadReplacements,
};

/**
 * The module path a notification's `type` concerns, or `null` when the
 * type is unknown OR the caller cannot read that module.
 */
export function targetPathFor(type: NotificationType | string, permissions: readonly string[]): string | null {
  const path = DESTINATION_BY_TYPE[type as NotificationType];
  if (path === undefined) return null;

  const canRead = CAN_READ_PATH[path];
  if (canRead === undefined || !canRead(permissions)) return null;

  return path;
}
